import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, Check, X } from 'lucide-react'
import { supabase } from '../../utils/supabase'
import { captureUserLocation } from '../../utils/location'

export default function AuthCallback() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [status, setStatus] = useState('processing') // processing, success, error
    const [message, setMessage] = useState('')

    useEffect(() => {
        const handleCallback = async () => {
            try {
                const { data, error } = await supabase.auth.getSession()

                if (error) {
                    throw error
                }

                const type = searchParams.get('type')

                if (type === 'recovery') {
                    // Redirect to password reset page
                    setStatus('success')
                    setMessage('Password reset verified! Redirecting...')
                    setTimeout(() => navigate('/settings?reset=true'), 2000)
                    return
                }

                if (data.session) {
                    // Capture user location in background (non-blocking)
                    captureUserLocation(data.session.user.id).catch(console.warn)

                    setStatus('success')
                    setMessage('Email verified! Redirecting to app...')
                    setTimeout(() => navigate('/app'), 2000)
                } else {
                    setStatus('success')
                    setMessage('Email verified! Please sign in.')
                    setTimeout(() => navigate('/signin'), 2000)
                }
            } catch (error) {
                console.error('Auth callback error:', error)
                setStatus('error')
                setMessage(error.message || 'Something went wrong')
            }
        }

        handleCallback()
    }, [navigate, searchParams])

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950">
            <div className="glass-card p-8 text-center max-w-md mx-4">
                {status === 'processing' && (
                    <>
                        <Loader2 className="w-12 h-12 text-primary-400 animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Processing...
                        </h2>
                        <p className="text-white/60">
                            Please wait while we verify your account.
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Check className="w-8 h-8 text-green-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Success!
                        </h2>
                        <p className="text-white/60">
                            {message}
                        </p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                            <X className="w-8 h-8 text-red-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Verification Failed
                        </h2>
                        <p className="text-white/60 mb-6">
                            {message}
                        </p>
                        <button
                            onClick={() => navigate('/signin')}
                            className="btn-primary"
                        >
                            Go to Sign In
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}
